import { Component, DestroyRef, EventEmitter, Inject, inject, Input, OnInit, Output } from '@angular/core';
import { faChevronDown, faChevronUp, faEyeSlash, faQuestion, faTrashAlt } from '@fortawesome/free-solid-svg-icons';
import {
    AddressModel,
    hasBackendAddrNumber,
    TextModel,
    TimesheetAddressModel,
    TimesheetAndExpensesInstanceToken,
} from '@evorait/pace-core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { catchError, of, throwError } from 'rxjs';
import { LogInstanceToken, LogInterface } from 'libs/pace-logging/src/lib/interfaces/log.interface';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'pace-address-select-expandable',
    templateUrl: './address-select-expandable.component.html',
    styleUrls: ['./address-select-expandable.component.scss'],
    standalone: false,
})
export class AddressSelectExpandableComponent implements OnInit {
    @Input() public readonly address: AddressModel;
    @Input() public readonly enableHideTimesheetAddress: boolean;
    @Input() public isEditable: boolean;
    @Input() public isExpanded = false;

    @Output() public toggleExpandEvent = new EventEmitter<void>();
    @Output() public selectAddressEvent = new EventEmitter<TimesheetAddressModel>();
    @Output() public deleteAddressEvent = new EventEmitter<void>();
    @Output() public saveAddressEvent = new EventEmitter<void>();
    @Output() public hideAddressEvent = new EventEmitter<void>();
    @Output() public formValidityChange = new EventEmitter<boolean>();
    @Output() public updateAddressEvent = new EventEmitter<TimesheetAddressModel>();
    @Output() public openHelpEvent = new EventEmitter<OpenHelpProperties>();

    private readonly timesheetAndExpensesService = inject(TimesheetAndExpensesInstanceToken);
    private readonly destroyRef = inject(DestroyRef);

    public readonly faChevronDown = faChevronDown;
    public readonly faChevronUp = faChevronUp;
    public readonly faTrashAlt = faTrashAlt;
    public readonly faEyeSlash = faEyeSlash;
    public readonly faQuestion = faQuestion;

    public addressFormGroup!: FormGroup;
    public addressFormChildren!: FormArray;
    public selectedAddress: TimesheetAddressModel;
    public isCollapsed = true;
    public constructor(
        private readonly formBuilder: FormBuilder,
        @Inject(LogInstanceToken) private readonly log: LogInterface,
    ) {}

    /**
     * Whether the address may be deleted, i.e. it exists on this device only. An address the backend
     * knows is hideable at most — deleting it would only bring it back with the next load.
     */
    public get allowToDelete(): boolean {
        return !hasBackendAddrNumber(this.address.addrNumber.value);
    }

    public get displayAddressText(): TextModel {
        if (this.isEditable) {
            return this.selectedAddress.addrText;
        }
        if (this.address.addrText.value) {
            return this.address.addrText;
        }
        if (this.address.name1.value) {
            return this.address.name1;
        }
        return new TextModel('');
    }

    public ngOnInit(): void {
        this.selectedAddress = this.address.getTimesheetAddress();

        if (!this.addressFormChildren) {
            this.addressFormChildren = this.formBuilder.array([]);
        }
        if (!this.addressFormGroup) {
            this.addressFormGroup = this.formBuilder.group({
                children: this.addressFormChildren,
            });
        }
    }

    public selectAddress(): void {
        (hasBackendAddrNumber(this.selectedAddress.addrNumber.value)
            ? of(undefined)
            : this.timesheetAndExpensesService.addAddressesAndSave([
                  AddressModel.fromTimesheetAddress(this.selectedAddress),
              ])
        )
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError((error) => {
                    this.log.error(
                        'Error in AddressSelectExpandableComponent -> selectAddress -> timesheetAndExpensesService.addAddress: ',
                        JSON.stringify(error),
                    );
                    return of(undefined);
                }),
            )
            .subscribe(() => this.selectAddressEvent.emit(this.selectedAddress));
    }

    public deleteAddress(): void {
        this.timesheetAndExpensesService
            .deleteAddress(this.address)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError((error) => {
                    this.log.error(
                        'Error in AddressSelectComponent -> deleteAddress -> timesheetAndExpensesService.deleteAddress: ',
                        JSON.stringify(error),
                    );
                    return throwError(() => error);
                }),
            )
            .subscribe(() => {
                this.deleteAddressEvent.emit();
            });
    }

    public openHelp(): void {
        const openHelpProperties: OpenHelpProperties = {
            allowToHide: this.address.allowToHide && this.enableHideTimesheetAddress,
            allowToDelete: this.allowToDelete,
        };
        this.openHelpEvent.emit(openHelpProperties);
    }

    public onAddressChange(): void {
        this.updateAddressEvent.emit(this.selectedAddress);
    }

    public hideAddress(): void {
        this.timesheetAndExpensesService
            .hideAddress(this.address)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError((error) => {
                    this.log.error(
                        'AddressSelectComponent:hideAddress: Error while hiding Address: ',
                        JSON.stringify(error),
                    );
                    return throwError(() => error);
                }),
            )
            .subscribe(() => this.hideAddressEvent.emit());
    }
}

interface OpenHelpProperties {
    allowToHide: boolean;
    allowToDelete: boolean;
}
